import React from 'react';
import PropTypes from 'prop-types';
import { SelectFilter } from './SelectFilter';

/**
 * `SelectFilter` speaking the option contract the repo's filter definitions
 * already use: options as `{ code, name }`, with an `'all'` sentinel that the
 * filter state stores as `''`.
 *
 * (Named confusingly close to `SelectFilter` — the difference is the contract.
 * `SelectFilter` is the primitive and passes values through untouched; this
 * adapts `utils/filters.js`-shaped options onto it. New code with `{ value,
 * label }` options wants `SelectFilter`.)
 *
 * Codes are stringified because some are booleans (`PRE_APPROVED_FILTER` uses
 * `true`/`false`) while the value read back out of the URL is always a string,
 * and the design system matches options with `===`. Without this, choosing
 * Pre-Approved = Yes and reloading left the list filtered but the control
 * blank. The native `<select>` this replaced stringified for us.
 *
 * Distinct from the legacy `CustomSelect`, a native `<select>` that the
 * PermanentJobV2 detail forms still consume.
 */
export const FilterSelect = ({ id, label, value, onChange, options }) => (
  <SelectFilter
    label={label || id}
    options={options.map(({ code, name }) => ({ value: String(code), label: name }))}
    value={value === '' || value === undefined || value === null ? 'all' : String(value)}
    onChange={(selected) => onChange(id, selected !== 'all' ? selected : '')}
  />
);

FilterSelect.propTypes = {
  /** Filter key; passed back as the first argument to `onChange`. */
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  /** Called as `(id, value)`, with `''` for the "All" option. */
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      name: PropTypes.string,
    }),
  ).isRequired,
};

FilterSelect.defaultProps = { label: undefined, value: '' };

export default FilterSelect;
